import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MenuBoxTracking from 'src/app/components/maps/MenuBoxTracking';
import { Box, Paper, useTheme, useMediaQuery } from '@material-ui/core';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import TrafficIcon from '@material-ui/icons/Traffic';
import SpeedIcon from '@material-ui/icons/Speed';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import {
  getListVehicleTrackingByUserId,
  getDetailVehicle,
  setListVehicleTracking,
  setStatisticVehicleStatus,
  getVehicleSystemStatus,
  getListVehiclesTrackingByDriver
} from 'src/features/vehicleSlice';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import { deduplicatedFetch } from 'src/app/utils/requestDeduplicator';
import CarStatusBar from 'src/app/components/maps/CarStatusBar';
import {
  MapContainer,
  TileLayer,
  LayersControl,
  useMap,
  useMapEvents,
  Marker,
  Tooltip,
  Circle
} from 'react-leaflet';
import L from 'leaflet';
import useSupercluster from 'use-supercluster';
import { useQuery } from 'src/app/hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { convertIdToDeviceType } from 'src/app/utils/commomService';
import { STATUS_API } from 'src/app/constant/config';
import _ from 'lodash';
import { Typography } from '@material-ui/core';
import MarkerManager from './MarkerManager';
import AnimationManager from './AnimationManager';
import ClusterManager from './ClusterManager';
import SystemStatusTable from './SystemStatus';
import { getLicensePlateDevice } from 'src/features/deviceSlice';
import HighwaysOverlayLayer from './HighwaysOverlayLayer';
import FuelStationsVietnam from './FuelStationsVietnam';
import TrackingCameraDock, { hasCameraDevice } from './TrackingCameraDock';

const MAX_ZOOM = 22;
const MIN_ZOOM = 6;
const DEFAULT_CENTER = [21.024683, 105.832495];
const REFRESH_INTERVAL = 10000;
const DESKTOP_TRACKING_MENU_WIDTH = 384;
const SELECTED_VEHICLE_FOLLOW_OFFSET_Y = 120;
const TOP_CONTROL_CENTER_Y = 38;
const STATUS_PANEL_HEIGHT = 36;
const INFO_BUTTON_SIZE = 40;
const MAP_TYPE_SWITCHER_SIZE = 44;
const STATUS_PANEL_TOP = TOP_CONTROL_CENTER_Y - STATUS_PANEL_HEIGHT / 2;
const INFO_BUTTON_TOP = TOP_CONTROL_CENTER_Y - INFO_BUTTON_SIZE / 2;
const MAP_TYPE_SWITCHER_TOP = TOP_CONTROL_CENTER_Y - MAP_TYPE_SWITCHER_SIZE / 2;
const MAP_TYPE_SWITCHER_RIGHT = 20;
const ACTION_STACK_RIGHT = MAP_TYPE_SWITCHER_RIGHT;
const RIGHT_CONTROL_GAP = 12;
const MAP_FULLSCREEN_BUTTON_TOP = MAP_TYPE_SWITCHER_TOP;
const ACTION_STACK_TOP =
  MAP_FULLSCREEN_BUTTON_TOP + MAP_TYPE_SWITCHER_SIZE + RIGHT_CONTROL_GAP;
const ACTION_STACK_BOTTOM = MAP_TYPE_SWITCHER_TOP;
const INFO_BUTTON_RIGHT = 88;
const MAP_TILE_MODES = {
  roadmap: {
    id: 'roadmap',
    previewClassName: 'tracking-map-switcher-preview-roadmap'
  },
  satellite: {
    id: 'satellite',
    previewClassName: 'tracking-map-switcher-preview-satellite'
  }
};

const TrackingMapTypeSwitcher = ({ activeMode, onChange, style }) => {
  const alternateMode =
    activeMode === 'satellite' ? MAP_TILE_MODES.roadmap : MAP_TILE_MODES.satellite;

  const handleToggle = () => onChange(alternateMode.id);

  return (
    <Box className="tracking-map-type-switcher" style={style}>
      <Box
        role="button"
        tabIndex={0}
        className={`tracking-map-type-switcher-tile ${alternateMode.previewClassName}`}
        onClick={handleToggle}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
          }
        }}
      />
    </Box>
  );
};

const TrackingMapFullscreenButton = ({ isFullscreen, onToggle, style }) => (
  <Box className="tracking-map-fullscreen-control" style={style}>
    <Box
      role="button"
      tabIndex={0}
      className="tracking-map-fullscreen-button"
      onClick={onToggle}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
    </Box>
  </Box>
);

const getFollowCenterLatLng = (map, position, offsetY = 0, zoom = map.getZoom()) => {
  if (!map || !position) return null;

  const latLng = L.latLng(position[0], position[1]);
  const size = map.getSize();
  const markerPoint = map.project(latLng, zoom);
  const nextCenterPoint = markerPoint.subtract([0, offsetY]);

  return map.unproject(nextCenterPoint, zoom);
};

const KeepMarkerInView = ({ position, isSelected, offsetY = 0 }) => {
  const map = useMap();

  useEffect(() => {
    if (isSelected && position) {
      const expectedCenter = getFollowCenterLatLng(map, position, offsetY);
      if (!expectedCenter) return;
      const center = map.getCenter();
      const isCentered =
        Math.abs(center.lat - expectedCenter.lat) < 0.000001 &&
        Math.abs(center.lng - expectedCenter.lng) < 0.000001;

      if (!isCentered) {
        map.setView(expectedCenter, map.getZoom(), { animate: false });
      }
    }
  }, [position, isSelected, map, offsetY]);

  return null;
};

const MapTracking = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const device_type_id = query.get('device_type_id');
  const agency_id = query.get('agency_id');
  const end_user_id = query.get('end_user_id');
  const search_agency_id = agency_id;
  const license_plate = query.get('license_plate');
  const lat = query.get('lat');
  const lng = query.get('lng');
  const zoomTo = query.get('zoomTo');

  const SYSTEM_STATUS_PAGE_SIZE = 15;

  const { isAdmin, isAgency, isUser, isDriver, userId, agencyId, adminId, driverId, dataLogin } = useSelector(
    state => state.authSlice
  );
  const listVehicleTracking = useSelector(
    state => state.vehicleSlice.listVehicleTracking
  );
  const detailVehicle = useSelector(state => state.vehicleSlice.detailVehicle);
  const statusGetDetail = useSelector(
    state => state.vehicleSlice.statusGetDetail
  );
  const vehicleNotifier = useSelector(
    state => state.notificationSlice.vehicleNotifier
  );
  const deviceType = convertIdToDeviceType(Number(device_type_id));
  const trackingId = adminId ?? agencyId;
  const agency_id_status = (isUser || isDriver) ? undefined : agency_id;

  const [mapRef, setMapRef] = useState(null);
  const [filterQuery, setFilterQuery] = useState({
    device_type_id: device_type_id ? device_type_id : undefined,
    agency_id: agency_id ? agency_id : undefined,
    end_user_id: end_user_id ? end_user_id : undefined,
    device_type: deviceType && (isUser || isDriver) ? deviceType : undefined
  });
  const [positionsInfoBox, setPositionsInfoBox] = useState({});
  const [detailedVehicleInfo, setDetailedVehicleInfo] = useState({});
  const [isPageVisible, setIsPageVisible] = useState(
    document.visibilityState === 'visible'
  );
  const [showMenu, setShowMenu] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [vehicleSelected, setVehicleSelected] = useState({});
  const [carStatus, setCarStatus] = useState('');
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [showStatus, setShowStatus] = useState(true);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(13);
  const [extraCount, setExtraCount] = useState(0);
  const [globalExtraCount, setGlobalExtraCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dataContext, setDataContext] = useState('');
  const [openSystemStatus, setOpenSystemStatus] = useState(false);
  const [systemStatusRows, setSystemStatusRows] = useState([]);
  const [systemStatusPage, setSystemStatusPage] = useState(1);
  const [isLoadingSystemStatus, setIsLoadingSystemStatus] = useState(false);
  const [hasMoreSystemStatus, setHasMoreSystemStatus] = useState(true);
  const [safeZoneMarker, setSafeZoneMarker] = useState(null);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showHighways, setShowHighways] = useState(false);
  const [showFuelStations, setShowFuelStations] = useState(false);
  const [mapTileMode, setMapTileMode] = useState('roadmap');
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [cameraViewCount, setCameraViewCount] = useState(4);
  const [endUsers, setEndUsers] = useState([]);
  const workspaceRef = useRef(null);
  const selectedVehicleFollowOffsetY = isMobile
    ? Math.round(SELECTED_VEHICLE_FOLLOW_OFFSET_Y * 0.7)
    : SELECTED_VEHICLE_FOLLOW_OFFSET_Y;
  const sidebarOffset = !isMobile && showMenu ? DESKTOP_TRACKING_MENU_WIDTH : 0;
  const selectedCameraVehicle = hasCameraDevice(vehicleSelected)
    ? vehicleSelected
    : hasCameraDevice(positionsInfoBox)
      ? positionsInfoBox
      : null;
  const selectedDeviceSerial =
    vehicleSelected?.device_serial || positionsInfoBox?.device_serial || '';
  const selectedCameraDeviceType =
    selectedCameraVehicle?.device_type ||
    selectedCameraVehicle?.vehicle?.device_type ||
    selectedCameraVehicle?.vehicle_type ||
    '';
  const hasSelectedCameraVehicle = Boolean(selectedCameraVehicle);
  const shouldKeepFocusOnPopupClose = hasCameraDevice(vehicleSelected);
  const mapLeftInset = sidebarOffset;
  const dynamicPadding = React.useMemo(() => {
    if (isMobile) {
      return {
        paddingTopLeft: [20, 20],
        paddingBottomRight: [20, 160]
      };
    }
    return {
      paddingTopLeft: [50, 50],
      paddingBottomRight: [50, 50]
    };
  }, [isMobile]);

  const matchedEndUser = React.useMemo(
    () =>
      endUsers.find(user => String(user.end_user_id) === String(end_user_id)),
    [endUsers, end_user_id]
  );
  const userIdCurrent =
    (isAdmin || isAgency) && end_user_id
      ? matchedEndUser?.id
      : dataLogin?.id;

  useEffect(() => {
    const syncMapFullscreenState = () => {
      setIsMapFullscreen(document.fullscreenElement === workspaceRef.current);
    };

    document.addEventListener('fullscreenchange', syncMapFullscreenState);
    syncMapFullscreenState();

    return () => {
      document.removeEventListener('fullscreenchange', syncMapFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (selectedCameraDeviceType === 'VGPS_2CAM' && cameraViewCount > 2) {
      setCameraViewCount(2);
      return;
    }

    if (selectedCameraDeviceType === 'VGPS_4CAM' && ![1, 2, 4].includes(cameraViewCount)) {
      setCameraViewCount(4);
    }
  }, [cameraViewCount, selectedCameraDeviceType]);

  useEffect(() => {
    if (isAdmin) setGlobalExtraCount(1880);
    else if (isAgency) {
      deduplicatedFetch(`/agencies/${trackingId}`)
        .then(res => {
          const name = (
            res?.data?.payload?.agency || res?.data?.payload
          )?.name?.trim();
          if (name === 'GPS Miền Nam' || name === 'Vmake') {
            setGlobalExtraCount(1880);
          }
        })
        .catch(() => setGlobalExtraCount(0));
    }
  }, [isAdmin, isAgency, trackingId]);

  useEffect(() => {
    const fetchAgencyName = async () => {
      const currentId = agency_id || trackingId;
      if (!currentId) {
        if (isAdmin) setExtraCount(1880);
        else setExtraCount(0);
        return;
      }
      try {
        const res = await deduplicatedFetch(`/agencies/${currentId}`);
        const payload = res?.data?.payload;
        const agency = payload?.agency || payload;
        const name = agency?.name?.trim();
        if (name === 'GPS Miền Nam' || name === 'Vmake') {
          setExtraCount(1880);
        } else if (!agency_id && isAdmin) {
          setExtraCount(1880);
        } else {
          setExtraCount(0);
        }
      } catch (e) {
        if (!agency_id && isAdmin) setExtraCount(1880);
        else setExtraCount(0);
      }
    };
    if (isAdmin || isAgency) {
      fetchAgencyName();
    } else {
      setExtraCount(0);
    }
  }, [agency_id, isAdmin, isAgency, trackingId]);

  useEffect(() => {
    dispatch(getLicensePlateDevice());
  }, [isUser, isAdmin, isAgency]);

  useEffect(() => {
    if (zoomTo === 'true' && lat && lng && mapRef) {
      const targetLat = parseFloat(lat);
      const targetLng = parseFloat(lng);


      if (!isNaN(targetLat) && !isNaN(targetLng)) {
        setSafeZoneMarker({ lat: targetLat, lng: targetLng });
        setZoom(20);

        mapRef.setView([targetLat, targetLng], 20, {
          animate: true,
          duration: 2
        });

        // navigate('/app/tracking', { replace: true });
      }
    }
  }, [lat, lng, zoomTo, mapRef, navigate]);

  const markerRef = useRef({});
  const animationRef = useRef({});
  const vehiclePositionsRef = useRef({});
  const lastKnownPositionsRef = useRef({});
  const vehicleIconsRef = useRef({});
  const clusterPositionsRef = useRef({});
  const lastUpdateRef = useRef({});
  const lastFitContextRef = useRef('');

  useEffect(() => {
    const handle = () =>
      setIsPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, []);

  const updateBoundsAndZoom = useCallback(
    _.debounce(() => {
      if (!mapRef) return;
      const b = mapRef.getBounds();
      setBounds([
        Math.min(b.getWest(), -180),
        Math.min(b.getSouth(), -90),
        Math.max(b.getEast(), 180),
        Math.max(b.getNorth(), 90)
      ]);
      setZoom(mapRef.getZoom());
    }, 200),
    [mapRef]
  );

  const points = React.useMemo(
    () =>
      listVehicleTracking.map((v, i) => ({
        type: 'Feature',
        properties: {
          cluster: false,
          vehicle: { ...v, lat: Number(v.lat), lng: Number(v.lng) },
          pointId: i
        },
        geometry: { type: 'Point', coordinates: [Number(v.lng), Number(v.lat)] }
      })),
    [listVehicleTracking]
  );

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 60, maxZoom: 18, minZoom: MIN_ZOOM }
  });

  const clusterCounts = React.useMemo(() => {
    const counts = {},
      singleVehicles = {};
    clusters.forEach(c => {
      if (!c.properties.cluster) return;
      const leaves = supercluster.getLeaves(c.properties.cluster_id, Infinity);
      const filtered = leaves.filter(l => {
        const v = l.properties.vehicle;
        if (vehicleSelected.device_id === v.device_id) return false;
        return (
          (!carStatus ||
            v.device_status?.toLowerCase() === carStatus.toLowerCase()) &&
          (!deviceType || v.vehicle_type === deviceType)
        );
      });
      counts[c.properties.cluster_id] = filtered.length;
      if (filtered.length === 1)
        singleVehicles[c.properties.cluster_id] =
          filtered[0].properties.vehicle;
    });
    return { counts, singleVehicles };
  }, [clusters, supercluster, carStatus, deviceType, vehicleSelected]);

  const fetchData = async () => {
    if (trackingId) {
      const { agency_id, ...rest } = filterQuery;

      const vehiclesQueryObj = { ...rest };
      if (agency_id) vehiclesQueryObj.agency_id = agency_id;
      try {
        const [resFalse, resTrue] = await Promise.all([
          deduplicatedFetch(
            `/vehicles/tracking/agency/${trackingId}?end_user=false&page_size=1000${_convertObjectToQuery(vehiclesQueryObj)
              ? '&' + _convertObjectToQuery(vehiclesQueryObj)
              : ''
            }`
          ),
          deduplicatedFetch(
            `/agencies/${trackingId}/enduser?page_size=1000${_convertObjectToQuery(rest)
              ? '&' + _convertObjectToQuery(rest)
              : ''
            }`
          )
        ]);
        setEndUsers(
          resTrue?.data?.payload?.users || []
        );
        const dataFalse = resFalse?.data?.payload?.latest_periodics || [];
        const dataTrue = resTrue?.data?.payload?.latest_periodics || [];
        dispatch(setListVehicleTracking([...dataFalse, ...dataTrue]));
        setDataContext(
          `${agency_id || ''}-${end_user_id ||
          ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
        );
      } catch (e) {
        console.error(e);
      }
    }
  };
  const fetchDataByUserId = id =>
    dispatch(
      getListVehicleTrackingByUserId({
        id: id || userId,
        data: {
          device_type_id: filterQuery.device_type_id,
          agency_id: agency_id ? agency_id : undefined,
          page_size: 1000
        }
      })
    );

  const fetchDataByDriver = () =>
    dispatch(
      getListVehiclesTrackingByDriver({
        device_type_id: filterQuery.device_type_id,
        device_type: filterQuery.device_type,
        page_size: 1000
      })
    );

  const fetchEndUsersByAgencyId = async currentAgencyId => {
    if (!currentAgencyId || !(isAdmin || isAgency)) {
      setEndUsers([]);
      return [];
    }

    try {
      const res = await deduplicatedFetch(
        `/agencies/${currentAgencyId}/enduser?page_size=1000`
      );
      const users =
        res?.data?.payload?.users || res?.data?.payload?.data || [];
      setEndUsers(users);
      return users;
    } catch (error) {
      setEndUsers([]);
      return [];
    }
  };

  const handleNodeClick = node => {
    const currentSearchAgencyId = query.get('search_agency_id');

    const params = new URLSearchParams();

    if (node.type === 'agency') {
      params.set('agency_id', node.id);

      if (currentSearchAgencyId) {
        params.set('search_agency_id', currentSearchAgencyId);
      }
    } else if (node.type === 'end_user') {
      params.set('end_user_id', node.endUserId);
      if (node.agencyId) params.set('agency_id', node.agencyId);
      if (currentSearchAgencyId)
        params.set('search_agency_id', currentSearchAgencyId);
    } else if (node.type === 'root') {
      if (currentSearchAgencyId)
        params.set('search_agency_id', currentSearchAgencyId);
    }

    if (device_type_id) params.set('device_type_id', device_type_id);

    if (node.id === agency_id || node.endUserId === end_user_id) {
      setRefreshKey(prev => prev + 1);
    }

    const search = params.toString();
    navigate(search ? `/app/tracking?${search}` : '/app/tracking');
  };

  useEffect(() => {
    const load = async () => {
      const {
        agency_id: _,
        search_agency_id: __,
        end_user_id: ___,
        ...rest
      } = filterQuery;

      if (end_user_id) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        if (isAdmin || isAgency) {
          await fetchEndUsersByAgencyId(agency_id);
        } else {
          setEndUsers([]);
        }
        dispatch(setListVehicleTracking([]));
        fetchDataByUserId(end_user_id);
      } else if (agency_id && (isAdmin || isAgency)) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        dispatch(setListVehicleTracking([]));
        try {
          const [resFalse, resTrue] = await Promise.all([
            deduplicatedFetch(
              `/vehicles/tracking/agency/${agency_id}?end_user=false&page_size=1000${_convertObjectToQuery(rest)
                ? '&' + _convertObjectToQuery(rest)
                : ''
              }`
            ),
            deduplicatedFetch(
              `/agencies/${agency_id}/enduser?page_size=1000${_convertObjectToQuery(rest)
                ? '&' + _convertObjectToQuery(rest)
                : ''
              }`
            )
          ]);
          setEndUsers(
            resTrue?.data?.payload?.users || []
          );

          const dataFalse = resFalse?.data?.payload?.latest_periodics || [];
          const dataTrue = resTrue?.data?.payload?.latest_periodics || [];
          dispatch(setListVehicleTracking([...dataFalse, ...dataTrue]));
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        } catch (e) {
          console.error(e);
        }
      } else {
        setEndUsers([]);
        if (isDriver) {
          dispatch(setListVehicleTracking([]));
          await fetchDataByDriver();
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        } else if (isAdmin || isUser) {
          dispatch(setListVehicleTracking([]));
          await fetchData();
          if (isUser) {
            fetchDataByUserId();
          }
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        } else if (isAgency) {
          setCarStatus('');
          dispatch(setListVehicleTracking([]));
          dispatch(setStatisticVehicleStatus(null));
          setDataContext(
            `${agency_id || ''}-${end_user_id ||
            ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
          );
        }
      }
    };
    load();
  }, [
    isAdmin,
    isAgency,
    isUser,
    isDriver,
    adminId,
    agency_id,
    end_user_id,
    device_type_id,
    refreshKey
  ]);

  const statusGetListTracking = useSelector(
    state => state.vehicleSlice.statusGetListTracking
  );
  useEffect(() => {
    if (statusGetListTracking === STATUS_API.SUCCESS) {
      setDataContext(
        `${agency_id || ''}-${end_user_id ||
        ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`
      );
    }
  }, [
    statusGetListTracking,
    agency_id,
    end_user_id,
    carStatus,
    device_type_id,
    refreshKey
  ]);

  const handleMarkerClick = useCallback(
    vehicle => {
      if (!vehicle) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        setDetailedVehicleInfo({});
        setIsLoadingDetail(false);
        Object.values(animationRef.current).forEach(cancelAnimationFrame);
        animationRef.current = {};

        const params = new URLSearchParams(window.location.search);
        if (params.has('license_plate')) {
          params.delete('license_plate');
          const search = params.toString();
          navigate(search ? `/app/tracking?${search}` : '/app/tracking');
        }
        return;
      }

      if (vehicle.debt) {
        return;
      }
      const isSameVehicle = vehicleSelected?.device_id === vehicle.device_id;
      if (isSameVehicle) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        setDetailedVehicleInfo({});
        setIsLoadingDetail(false);
        Object.values(animationRef.current).forEach(cancelAnimationFrame);
        animationRef.current = {};

        const params = new URLSearchParams(window.location.search);
        if (params.has('license_plate')) {
          params.delete('license_plate');
          const search = params.toString();
          navigate(search ? `/app/tracking?${search}` : '/app/tracking');
        }
        return;
      }

      const lat = Number(vehicle.lat) || 0;
      const lng = Number(vehicle.lng) || 0;

      Object.values(animationRef.current).forEach(cancelAnimationFrame);
      animationRef.current = {};

      setVehicleSelected(vehicle);
      setDetailedVehicleInfo({});
      setIsLoadingDetail(true);
      const newInfoBox = { ...vehicle, lat, lng };
      if (!_.isEqual(positionsInfoBox, newInfoBox)) {
        setPositionsInfoBox(newInfoBox);
      }
      if (mapRef) {
        const nextZoom = Math.max(mapRef.getZoom(), 18);
        const nextCenter = getFollowCenterLatLng(
          mapRef,
          [lat, lng],
          selectedVehicleFollowOffsetY,
          nextZoom
        );
        mapRef.setView(nextCenter || [lat, lng], nextZoom, {
          animate: true
        });
      }
    },
    [mapRef, positionsInfoBox, selectedVehicleFollowOffsetY, vehicleSelected]
  );

  const MapClickHandler = () => {
    useMapEvents({
      click() {
        if (vehicleSelected.device_id) {
          if (shouldKeepFocusOnPopupClose) {
            return;
          }
          handleMarkerClick(null);
        }
      },
      popupclose() {
        if (vehicleSelected.device_id) {
          if (shouldKeepFocusOnPopupClose) {
            return;
          }
          handleMarkerClick(null);
        }
      }
    });
    return null;
  };

  useEffect(() => {
    if (!vehicleSelected?.device_id) return;
    const updated = listVehicleTracking.find(
      v => v.device_id === vehicleSelected.device_id
    );
    if (updated) {
      const lat = Number(updated.lat),
        lng = Number(updated.lng);
      setPositionsInfoBox(prev => ({ ...prev, ...updated, lat, lng }));
      setVehicleSelected(prev => ({ ...prev, ...updated, lat, lng }));
    }
  }, [listVehicleTracking, vehicleSelected?.speed_gps]);

  useEffect(() => {
    if (!vehicleSelected.device_id) {
      setDetailedVehicleInfo({});
      setIsLoadingDetail(false);
      return;
    }
    const fetch = () => {
      setIsLoadingDetail(true);
      dispatch(getDetailVehicle({ id: vehicleSelected.device_id }));
    };
    fetch();
    const id = setInterval(fetch, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [vehicleSelected.device_id, dispatch]);

  useEffect(() => {
    if (statusGetDetail === STATUS_API.PENDING) setIsLoadingDetail(true);
    else if (statusGetDetail === STATUS_API.SUCCESS && detailVehicle) {
      const data = {
        ...detailVehicle,
        lat: Number(detailVehicle.lat),
        lng: Number(detailVehicle.lng)
      };
      setDetailedVehicleInfo(data);
      setIsLoadingDetail(false);
    } else if (statusGetDetail === STATUS_API.ERROR) {
      setDetailedVehicleInfo(prev => ({ ...prev, daily_distance: 0 }));
      setIsLoadingDetail(false);
    }
  }, [detailVehicle, statusGetDetail]);

  useEffect(() => {
    if (vehicleNotifier && vehicleNotifier[0]) {
      const vehicle = vehicleNotifier[0];
      handleMarkerClick(vehicle);
    }
  }, [vehicleNotifier]);

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(pos => {
      const loc = [pos.coords.latitude, pos.coords.longitude];
      setCurrentLocation(loc);
      mapRef?.flyTo(loc, 15);
    });
  };

  const displayLocationIcon = () =>
    L.icon({
      iconUrl: `/static/iconSvg/gps.svg`,
      iconSize: [25, 25],
      iconAnchor: [0, 25]
    });

  useEffect(() => {
    const newQuery = {
      device_type_id: device_type_id ? device_type_id : undefined,
      agency_id: agency_id ? agency_id : undefined,
      end_user_id: end_user_id ? end_user_id : undefined,
      device_type: deviceType && (isUser || isDriver) ? deviceType : undefined
    };
    if (!_.isEqual(filterQuery, newQuery)) setFilterQuery(newQuery);
  }, [device_type_id, agency_id, deviceType, isUser, isDriver]);

  useEffect(() => {
    if (!isUser && license_plate && listVehicleTracking.length > 0) {
      const found = listVehicleTracking.find(v => {
        const lp = v.vehicle?.license_plate || v.license_plate || '';
        return lp.toUpperCase() === license_plate.toUpperCase();
      });

      if (found && found.device_id !== vehicleSelected?.device_id) {
        handleMarkerClick(found);
      }
    }
  }, [
    license_plate,
    listVehicleTracking,
    handleMarkerClick,
    vehicleSelected?.device_id,
    isUser
  ]);

  const statisticVehicleStatus = useSelector(
    state => state.vehicleSlice.statisticVehicleStatus
  );

  const [rootDeviceCount, setRootDeviceCount] = useState(null);

  useEffect(() => {
    if (isAdmin || isAgency) {
      deduplicatedFetch(`/agencies?parent_id=${trackingId || ''}&page_size=1`)
        .then(res => {
          const payload = res?.data?.payload;
          setRootDeviceCount(
            payload?.deviceActive ?? payload?.numberOfDevices ?? null
          );
        })
        .catch(() => { });
    }
  }, [trackingId, isAdmin, isAgency]);

  const baseDeviceCount =
    statisticVehicleStatus?.total ??
    statisticVehicleStatus?.total_devices ??
    listVehicleTracking.length;

  const isDataLoaded =
    rootDeviceCount !== null || statusGetListTracking === STATUS_API.SUCCESS;
  const effectiveGlobalExtraCount = isDataLoaded ? globalExtraCount : 0;
  const effectiveExtraCount = isDataLoaded ? extraCount : 0;

  const totalDeviceCount =
    (rootDeviceCount !== null ? rootDeviceCount : baseDeviceCount) +
    effectiveGlobalExtraCount;

  useEffect(() => {
    if (!mapRef) return;
    updateBoundsAndZoom();
    mapRef.on('moveend', updateBoundsAndZoom);
    return () => mapRef.off('moveend', updateBoundsAndZoom);
  }, [mapRef, updateBoundsAndZoom]);

  useEffect(() => {
    if (!mapRef) return undefined;

    const timer = window.setTimeout(() => {
      mapRef.invalidateSize(true);
    }, 280);

    return () => window.clearTimeout(timer);
  }, [cameraViewCount, hasSelectedCameraVehicle, isMobile, mapRef, showMenu]);
  useEffect(() => {
    if (listVehicleTracking.length > 0 && mapRef) {
      const currentContext = `${agency_id || ''}-${end_user_id ||
        ''}-${carStatus}-${device_type_id || ''}-${refreshKey}`;
      if (
        dataContext === currentContext &&
        currentContext !== lastFitContextRef.current
      ) {
        const filtered = listVehicleTracking.filter(
          v =>
            (!carStatus ||
              v.device_status?.toLowerCase() === carStatus.toLowerCase()) &&
            (!deviceType || v.vehicle_type === deviceType)
        );

        const points = filtered
          .map(v => [Number(v.lat), Number(v.lng)])
          .filter(
            p => !isNaN(p[0]) && !isNaN(p[1]) && (p[0] !== 0 || p[1] !== 0)
          );

        if (points.length > 0) {
          const bounds = L.latLngBounds(points);
          mapRef.fitBounds(bounds, { ...dynamicPadding, maxZoom: 18 });
          lastFitContextRef.current = currentContext;
        }
      }
    }
  }, [
    listVehicleTracking,
    dataContext,
    agency_id,
    end_user_id,
    carStatus,
    device_type_id,
    mapRef,
    refreshKey,
    deviceType
  ]);

  useEffect(() => {
    const marker = markerRef.current[vehicleSelected.device_id];
    if (marker && !marker.isPopupOpen()) marker.openPopup();
  }, [vehicleSelected?.device_id]);

  const renderSelectedVehicleMarker = () => {
    if (!vehicleSelected.device_id) return null;
    const isVisible = !carStatus || carStatus === vehicleSelected.device_status;
    if (!isVisible) return null;
    const pos = vehiclePositionsRef.current[vehicleSelected.device_id] || [
      Number(vehicleSelected.lat),
      Number(vehicleSelected.lng)
    ];

    return (
      <React.Fragment key={vehicleSelected.device_id}>
        <MarkerManager
          vehicle={vehicleSelected}
          position={pos}
          isSelected={true}
          isInViewport={mapRef?.getBounds().contains(pos)}
          onClick={handleMarkerClick}
          markerRef={markerRef}
          vehicleIconsRef={vehicleIconsRef}
          positionsInfoBox={positionsInfoBox}
          detailedVehicleInfo={detailedVehicleInfo}
          isLoadingDetail={isLoadingDetail}
          opacity={1}
          mapRef={mapRef}
          popupAutoPan={false}
        />
        <KeepMarkerInView
          position={pos}
          isSelected={true}
          offsetY={selectedVehicleFollowOffsetY}
        />
      </React.Fragment>
    );
  };

  const fetchVehicleSystemStatus = async (
    agencyId,
    endUserId,
    page,
    isRefresh = false
  ) => {
    if (isLoadingSystemStatus || !endUserId) return;

    setIsLoadingSystemStatus(true);
    const paramsSystemStatus = {
      agency_id: agencyId,
      page,
      page_size: SYSTEM_STATUS_PAGE_SIZE
    };

    try {
      const action = await dispatch(
        getVehicleSystemStatus({ id: endUserId, data: paramsSystemStatus })
      );
      const nextRows = action?.payload?.payload?.latest_periodics || [];

      setSystemStatusRows(prev =>
        isRefresh ? nextRows : [...prev, ...nextRows]
      );
      setSystemStatusPage(page);
      setHasMoreSystemStatus(nextRows.length >= SYSTEM_STATUS_PAGE_SIZE);
    } finally {
      setIsLoadingSystemStatus(false);
    }
  };

  const handleViewTableStatus = async () => {
    setOpenSystemStatus(true);
    setHasMoreSystemStatus(true);
    await fetchVehicleSystemStatus(agency_id_status, userIdCurrent, 1, true);
  };

  const handleLoadMoreSystemStatus = () => {
    if (!hasMoreSystemStatus || isLoadingSystemStatus) return;
    fetchVehicleSystemStatus(
      agency_id_status,
      userIdCurrent,
      systemStatusPage + 1
    );
  };

  const handleCloseSystemStatus = () => {
    setOpenSystemStatus(false);
    setSystemStatusRows([]);
    setSystemStatusPage(1);
    setHasMoreSystemStatus(true);
  };

  const handleToggleMapFullscreen = useCallback(async () => {
    const workspaceEl = workspaceRef.current;
    if (!workspaceEl) return;

    if (document.fullscreenElement === workspaceEl) {
      await document.exitFullscreen?.();
      return;
    }

    await workspaceEl.requestFullscreen?.();
  }, []);

  return (
    <Box className="tracking-map-workspace" ref={workspaceRef}>
      <Box
        className="tracking-map-action-stack tracking-map-action-stack-top"
        style={{
          position: 'absolute',
          top: `${ACTION_STACK_TOP}px`,
          right: `${ACTION_STACK_RIGHT}px`,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${RIGHT_CONTROL_GAP}px`
        }}
      >
        <Box
          title="Mật độ giao thông"
          onClick={() => setShowTraffic(!showTraffic)}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: showTraffic ? '#d9042f' : '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <TrafficIcon style={{ color: showTraffic ? '#fff' : '#d9042f', fontSize: 24 }} />
        </Box>

        <Box
          title="Cao tốc Việt Nam"
          onClick={() => setShowHighways(prev => !prev)}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: showHighways ? '#d9042f' : '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <SpeedIcon style={{ color: showHighways ? '#fff' : '#d9042f', fontSize: 24 }} />
        </Box>

        <Box
          title="Trạm xăng"
          onClick={() => setShowFuelStations(prev => !prev)}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: showFuelStations ? '#d9042f' : '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <LocalGasStationIcon
            style={{ color: showFuelStations ? '#fff' : '#d9042f', fontSize: 24 }}
          />
        </Box>
      </Box>

      <Box
        className="tracking-map-action-stack tracking-map-action-stack-bottom"
        style={{
          position: 'absolute',
          right: `${ACTION_STACK_RIGHT}px`,
          bottom: `${ACTION_STACK_BOTTOM}px`,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${RIGHT_CONTROL_GAP}px`
        }}
      >
        <Box
          title="Danh sách phương tiện"
          onClick={handleViewTableStatus}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <DirectionsCarIcon style={{ color: '#d9042f', fontSize: 24 }} />
        </Box>

        <Box
          title="Vị trí hiện tại"
          onClick={getCurrentLocation}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <img
            src="/static/iconSvg/locationIconv2.svg"
            alt="location"
            style={{ width: 22, height: 22 }}
          />
        </Box>

        <TrackingMapTypeSwitcher
          activeMode={mapTileMode}
          onChange={setMapTileMode}
          style={{
            position: 'relative',
            top: 'auto',
            right: 'auto',
            zIndex: 'auto'
          }}
        />
      </Box>

      <MenuBoxTracking
        getVehicle={handleMarkerClick}
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        setCarStatus={setCarStatus}
        carStatus={carStatus}
        idSelected={vehicleSelected.device_id}
        onNodeClick={handleNodeClick}
        search_agency_id={search_agency_id}
        totalDeviceCount={totalDeviceCount}
        baseDeviceCount={baseDeviceCount}
        extraCount={effectiveExtraCount}
        onRefresh={() => setRefreshKey(k => k + 1)}
      />

      {!isMobile && selectedCameraVehicle && (
        <TrackingCameraDock
          deviceSerial={selectedDeviceSerial}
          deviceType={selectedCameraDeviceType}
          viewCount={cameraViewCount}
          showMenu={showMenu}
          isMapFullscreen={isMapFullscreen}
          leftOffset={sidebarOffset}
          onChangeViewCount={setCameraViewCount}
        />
      )}

      <Box
        className="tracking-status-panel"
        style={{
          position: 'absolute',
          top: STATUS_PANEL_TOP,
          right: isMobile ? '5%' : '160px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          width: isMobile
            ? showStatus
              ? '90%'
              : '0px'
            : showStatus
              ? '700px'
              : '0px',
          transformOrigin: 'right center',
          transform: 'translateX(0)',
          transition: 'width 300ms ease',
          overflow: 'hidden'
        }}
      >
        {showStatus && (
          <Paper
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              transition: 'all 0.3s ease',
              width: '100%'
            }}
          >
            <Box
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px 4px'
              }}
            >
              <Typography
                style={{
                  color: '#D9042F',
                  fontSize: '12px',
                  fontWeight: '700',
                  whiteSpace: 'nowrap',
                  paddingRight: '28px'
                }}
              >
                Tổng: {baseDeviceCount}
              </Typography>
              <Box style={{ flex: 1 }}>
                <CarStatusBar
                  carStatus={carStatus}
                  setCarStatus={setCarStatus}
                  statisticVehicleStatus={statisticVehicleStatus}
                />
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      <Box
        onClick={() => setShowStatus(!showStatus)}
        style={{
          position: 'absolute',
          top: INFO_BUTTON_TOP,
          right: INFO_BUTTON_RIGHT,
          zIndex: 500,
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: '#fff',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
      >
        <img
          src="/static/iconSvg/info.svg"
          alt="info"
          style={{ width: 24, height: 24 }}
        />
      </Box>

      <Box
        className="tracking-map-canvas"
        style={{
          top: 0,
          right: 0,
          bottom: 0,
          left: mapLeftInset
        }}
      >
        <TrackingMapFullscreenButton
          isFullscreen={isMapFullscreen}
          onToggle={handleToggleMapFullscreen}
          style={{
            top: `${MAP_FULLSCREEN_BUTTON_TOP}px`,
            right: `${MAP_TYPE_SWITCHER_RIGHT}px`
          }}
        />
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={13}
          maxZoom={MAX_ZOOM}
          minZoom={MIN_ZOOM}
          style={{ height: '100%', width: '100%' }}
          className="tracking-leaflet-map"
          zoomControl={false}
          scrollWheelZoom={true}
          whenCreated={setMapRef}
          zoomAnimation={true}
        >
          <TileLayer
            key={`${mapTileMode}-${showTraffic}`}
            url={`https://{s}.google.com/vt/lyrs=${mapTileMode === 'satellite'
                ? showTraffic
                  ? 'y,traffic'
                  : 'y'
                : showTraffic
                  ? 'm,traffic'
                  : 'm'
              }&hl=vi&x={x}&y={y}&z={z}`}
            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            maxZoom={MAX_ZOOM}
          />

          {showHighways && <HighwaysOverlayLayer />}
          {showFuelStations && <FuelStationsVietnam />}

          <MapClickHandler />

          <AnimationManager
            listVehicleTracking={listVehicleTracking}
            mapRef={mapRef}
            clusters={clusters}
            supercluster={supercluster}
            vehicleSelected={vehicleSelected}
            vehiclePositionsRef={vehiclePositionsRef}
            lastKnownPositionsRef={lastKnownPositionsRef}
            clusterPositionsRef={clusterPositionsRef}
            markerRef={markerRef}
            vehicleIconsRef={vehicleIconsRef}
            animationRef={animationRef}
            lastUpdateRef={lastUpdateRef}
            isPageVisible={isPageVisible}
            selectedVehicleFollowOffsetY={selectedVehicleFollowOffsetY}
          />

          <ClusterManager
            clusters={clusters}
            mapRef={mapRef}
            supercluster={supercluster}
            carStatus={carStatus}
            deviceType={deviceType}
            vehicleSelected={vehicleSelected}
            clusterCounts={clusterCounts}
            vehiclePositionsRef={vehiclePositionsRef}
            markerRef={markerRef}
            vehicleIconsRef={vehicleIconsRef}
            positionsInfoBox={positionsInfoBox}
            detailedVehicleInfo={detailedVehicleInfo}
            isLoadingDetail={isLoadingDetail}
            handleMarkerClick={handleMarkerClick}
          />
          {renderSelectedVehicleMarker()}

          <SystemStatusTable
            open={openSystemStatus}
            onClose={handleCloseSystemStatus}
            onRefresh={() =>
              fetchVehicleSystemStatus(agency_id_status, userIdCurrent, 1, true)
            }
            rows={systemStatusRows}
            onRowClick={row => {
              handleMarkerClick(row.rawData || row);
              setOpenSystemStatus(false);
            }}
            onLoadMore={handleLoadMoreSystemStatus}
            isLoading={isLoadingSystemStatus}
            hasMore={hasMoreSystemStatus}
          />

          {currentLocation && (
            <Marker icon={displayLocationIcon()} position={currentLocation}>
              <Tooltip direction="center" permanent opacity={1}>
                <Box
                  style={{
                    backgroundColor: 'rgba(19,19,19,0.5)',
                    color: 'white',
                    borderRadius: '5px',
                    padding: '2px 6px',
                    fontSize: '12px'
                  }}
                >
                  Vị trí của bạn
                </Box>
              </Tooltip>
            </Marker>
          )}

          {safeZoneMarker && (
            <Circle
              center={[safeZoneMarker.lat, safeZoneMarker.lng]}
              radius={500}
              pathOptions={{
                color: '#ee0434',
                fillColor: '#ee0434',
                fillOpacity: 0.2,
                weight: 2
              }}
            >
              <Tooltip direction="bottom" opacity={1}>
                <Box
                  style={{
                    backgroundColor: '#ee0434',
                    color: 'white',
                    borderRadius: '5px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  Vùng an toàn
                </Box>
              </Tooltip>
            </Circle>
          )}
        </MapContainer>
      </Box>
    </Box>
  );
};

export default React.memo(MapTracking);
